// ============================================================
// Projektvorlagen (SPEC §50.8, Wanderung 044) — ein Gerüst aus Abgaben und
// Meilensteinen je Projekttyp.
//
// Die Geschäftsführung und die Admins pflegen die Vorlagen in der Verwaltung
// (Reiter „Projektvorlagen"); wer ein Projekt anlegt, wählt optional eine aus
// („Vorlage anwenden", `ProjektNeu.vorlage`), und der Server legt in derselben
// Transaktion je Eintrag eine Abgabe an. Danach ist alles frei änderbar — es
// bleibt keine Bindung an die Vorlage (§50.8).
//
// Eine eigene Datei neben `hooks.ts` aus demselben Grund wie `cockpit.ts`. Das
// Anlegen selbst geht weiter über `useProjektNeu` (hooks.ts) — der Rumpf trägt
// nur ein Feld mehr.
// ============================================================
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "./client";
import type { ProjektVorlage, ProjektVorlageAendern, ProjektVorlageNeu } from "./types";

/* ========================================================== Schlüssel */

export const vorlagenSchluessel = {
  /** Die eine Liste — `lib/ereignisse.ts` entwertet sie bei jeder Meldung `projekt_vorlage`
   *  (anweisungsweise, ohne Projektkennung, 044). */
  alle: ["projektvorlagen"] as const,
};

/* ========================================================== Zahlen (dieselben wie im Server, §50.8) */

/** `ProjektVorlage.name`: 1–80 Zeichen. */
export const VORLAGE_NAME_MAX = 80;
/** Höchstens so viele Einträge trägt eine Vorlage. */
export const VORLAGE_EINTRAEGE_MAX = 40;
/** `ProjektVorlageEintrag.titel`: 1–160 Zeichen (wie `Abgabe.titel`). */
export const VORLAGE_TITEL_MAX = 160;
/** `ProjektVorlageEintrag.versatz_tage`: 0 bis 1825 (fünf Jahre). */
export const VORLAGE_VERSATZ_MAX = 1825;
/** `VorlageAnwenden.start`: der Bereich, den der Server annimmt. */
export const VORLAGE_START_FRUEHESTENS = "2000-01-01";
export const VORLAGE_START_SPAETESTENS = "2100-12-31";

/** Vorlagen ändern sich selten; der Ereignisstrom entwertet ohnehin. */
const FRISCH_MS = 5 * 60_000;

/* ========================================================== Lesen */

/** `GET /projektvorlagen` → alle Vorlagen (nach `sortierung`, `name`). Jede angemeldete Person —
 *  das Anlegeformular steht seit §36 jeder Rolle offen. */
export function useProjektVorlagen(aktiv = true): UseQueryResult<ProjektVorlage[]> {
  return useQuery({
    queryKey: vorlagenSchluessel.alle,
    queryFn: () => api.get<ProjektVorlage[]>("/projektvorlagen"),
    enabled: aktiv,
    staleTime: FRISCH_MS,
  });
}

/* ========================================================== Pflegen (Admin, GF) */

/** `POST /projektvorlagen` → 201 `ProjektVorlage`. 403 `Das darf nur ein Admin.` für alle anderen. */
export function useVorlageNeu(): UseMutationResult<ProjektVorlage, Error, ProjektVorlageNeu> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ProjektVorlageNeu) => api.post<ProjektVorlage>("/projektvorlagen", b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: vorlagenSchluessel.alle }),
  });
}

/** `PATCH /projektvorlagen/{id}` → `ProjektVorlage`. `eintraege` ersetzt die ganze Liste. */
export function useVorlageAendern(): UseMutationResult<ProjektVorlage, Error, ProjektVorlageAendern & { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: ProjektVorlageAendern & { id: string }) =>
      api.patch<ProjektVorlage>(`/projektvorlagen/${id}`, b satisfies ProjektVorlageAendern),
    onSuccess: () => void qc.invalidateQueries({ queryKey: vorlagenSchluessel.alle }),
  });
}

/** `DELETE /projektvorlagen/{id}` → 204. Projekte, die sie benutzt haben, behalten ihre Abgaben. */
export function useVorlageLoeschen(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/projektvorlagen/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: vorlagenSchluessel.alle }),
  });
}
